import classNames from 'classnames'
import { Calendar, CalendarChangeEvent } from 'primereact/calendar'
import { FC } from 'react'

const toIsoDate = (date: Date | null | undefined): string => {
  if (!date) return ''
  const yyyy = date.getFullYear().toString().padStart(4, '0')
  const mm = (date.getMonth() + 1).toString().padStart(2, '0')
  const dd = date.getDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const fromIsoDate = (value: string | undefined): Date | null => {
  if (!value) return null
  const [yyyy, mm, dd] = value.split('-').map(Number)
  if (!yyyy || !mm || !dd) return null
  return new Date(yyyy, mm - 1, dd)
}

type AppDatePickerSingleProps = {
  selectionMode?: 'single'
  value?: string
  onChange?: (value: string) => void
}

type AppDatePickerRangeProps = {
  selectionMode: 'range'
  value?: [string, string]
  onChange?: (value: [string, string]) => void
}

type AppDatePickerCommonProps = {
  label?: string
  placeholder?: string
  disabled?: boolean
  warningText?: string
  helpText?: boolean
  minDate?: string
  maxDate?: string
  className?: string
  panelClassName?: string
  inputClassName?: string
  appendTo?: 'self' | HTMLElement
  inputId?: string
  'aria-label'?: string
}

export type AppDatePickerProps = AppDatePickerCommonProps & (AppDatePickerSingleProps | AppDatePickerRangeProps)

const DEFAULT_INPUT_CLASS =
  'h-12 w-full bg-white border-[0.1rem] border-solid border-orange rounded-[0.8rem] py-[0.8rem] px-[1.2rem] text-[1.2rem] text-dark-blue'

export const AppDatePicker: FC<AppDatePickerProps> = props => {
  const {
    label,
    placeholder = 'mm/dd/yyyy',
    disabled = false,
    warningText,
    helpText,
    minDate,
    maxDate,
    className,
    panelClassName,
    inputClassName,
    appendTo,
    inputId,
  } = props

  const isRange = props.selectionMode === 'range'

  const calendarValue: Date | (Date | null)[] | null = isRange
    ? [fromIsoDate(props.value?.[0]), fromIsoDate(props.value?.[1])]
    : fromIsoDate(props.value)

  const handleChange = (event: CalendarChangeEvent): void => {
    if (isRange) {
      const next = Array.isArray(event.value) ? event.value : [null, null]
      const [start, end] = next
      props.onChange?.([toIsoDate(start as Date | null), toIsoDate(end as Date | null)])
      return
    }
    if (event.value instanceof Date) {
      props.onChange?.(toIsoDate(event.value))
    } else if (event.value == null) {
      props.onChange?.('')
    }
  }

  return (
    <div className={`app-date-picker-wrapper flex flex-col gap-2 ${className ?? ''}`}>
      {label && (
        <label htmlFor={inputId} className="app-date-picker-label">
          {label}
        </label>
      )}
      <Calendar
        inputId={inputId}
        aria-label={props['aria-label']}
        disabled={disabled}
        value={calendarValue as Date | Date[] | null | undefined}
        minDate={fromIsoDate(minDate) ?? undefined}
        maxDate={fromIsoDate(maxDate) ?? undefined}
        placeholder={placeholder}
        selectionMode={isRange ? 'range' : 'single'}
        appendTo={appendTo}
        inputClassName={classNames(DEFAULT_INPUT_CLASS, { 'border-toast-hover': disabled }, inputClassName)}
        panelClassName={`text-lg ${panelClassName ?? ''}`}
        className="app-date-picker app-date-picker-icon"
        onChange={handleChange}
        readOnlyInput={false}
      />
      {warningText ? (
        <p className="app-input-warning-text">{warningText}</p>
      ) : (
        helpText && (
          <p className="app-input-warning-text">
            <br />
          </p>
        )
      )}
    </div>
  )
}
